import React from "react";
import "./Service_pages.scss";
import img from "./services/tooth_restoration.png";
import dentalImage from "./services/dental.png";
import {
    FaCheckCircle, FaExclamationTriangle, FaClinicMedical, FaUserMd,
    FaShieldAlt, FaMicroscope, FaStar,
    FaTooth, FaLink, FaTools, FaBrush, FaCalendarAlt, FaHistory,
    FaGem, 
} from 'react-icons/fa';
import {
    MdOutlineHealthAndSafety, MdEmergency,
    MdHealing, MdBrokenImage,
    MdRestore, MdConstruction, MdFormatPaint
} from "react-icons/md";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const ToothRestoration = () => {

    const benefits = [
        {
            category: "Functionality",
            benefit: "Restores tooth function",
            description: "Enables proper chewing and biting, improving overall digestive health."
        },
        {
            category: "Aesthetics",
            benefit: "Improves smile appearance",
            description: "Repairs broken or discolored teeth for a more confident smile."
        },
        {
            category: "Prevention",
            benefit: "Prevents further damage",
            description: "Stops decay from spreading to surrounding healthy teeth and tissues."
        },
        {
            category: "Alignment",
            benefit: "Supports oral health",
            description: "Maintains proper alignment and prevents jaw function issues."
        },
        {
            category: "Self-Esteem",
            benefit: "Boosts confidence",
            description: "A healthy, beautiful smile improves self-esteem and quality of life."
        }
    ];

    const damageCauses = [
        {
            title: "Tooth Decay",
            icon: <MdBrokenImage />,
            desc: "Cavities caused by bacteria break down the enamel and dentin, requiring fillings or crowns."
        },
        {
            title: "Tooth Fractures",
            icon: <FaExclamationTriangle />,
            desc: "Accidents, sports injuries, or biting hard foods can crack or chip teeth."
        },
        {
            title: "Tooth Wear",
            icon: <FaHistory />,
            desc: "Grinding teeth (bruxism) or aging can gradually wear down enamel."
        },
        {
            title: "Gum Disease",
            icon: <MdOutlineHealthAndSafety />,
            desc: "Periodontal disease can weaken tooth support and lead to tooth loss."
        },
        {
            title: "Trauma or Injury",
            icon: <MdEmergency />,
            desc: "Dental trauma may require immediate restorative treatment to save the tooth."
        }
    ];

    const treatmentTypes = [
        {
            title: "Dental Fillings",
            icon: <MdFormatPaint />,
            desc: "Used to repair cavities caused by decay. Materials include composite resin, amalgam, ceramic, and glass ionomer. Composite resin is popular as it matches the natural tooth color."
        },
        {
            title: "Dental Crowns",
            icon: <FaGem />,
            desc: "Crowns are protective caps placed over damaged or weak teeth. They are commonly used when: A tooth is severely decayed, A tooth has undergone root canal treatment, A tooth is cracked or broken. Crowns restore strength, shape, and appearance."
        },
        {
            title: "Dental Bridges",
            icon: <FaLink />,
            desc: "Dental bridges replace one or more missing teeth by anchoring artificial teeth to adjacent natural teeth. Benefits include: Restoring chewing ability, Maintaining facial structure, Preventing teeth from shifting."
        },
        {
            title: "Dental Implants",
            icon: <MdConstruction />,
            desc: "Dental implants are one of the most advanced solutions for replacing missing teeth. An implant consists of: Titanium screw placed in the jawbone, Abutment connector, Artificial crown. Implants function like natural teeth and can last for decades with proper care."
        },
        {
            title: "Root Canal Treatment",
            icon: <FaMicroscope />,
            desc: "Root canal treatment is performed when the pulp inside a tooth becomes infected. During the procedure: The infected pulp is removed. The root canals are cleaned and disinfected. The tooth is sealed and often restored with a crown. This treatment helps save the natural tooth and prevents extraction."
        },
        {
            title: "Dentures",
            icon: <FaTooth />,
            desc: "Dentures are removable prosthetic devices used to replace multiple missing teeth. Types include: Full dentures, Partial dentures, Implant-supported dentures. Dentures improve speech, chewing, and facial appearance."
        },
        {
            title: "Dental Veneers",
            icon: <FaStar />,
            desc: "Dental veneers are thin shells placed on the front surface of teeth to improve their appearance. They are ideal for correcting: Chipped teeth, Discoloration, Minor misalignment, Uneven teeth. Veneers provide a quick cosmetic smile transformation."
        },
        {
            title: "Inlays & Onlays",
            icon: <MdHealing />,
            desc: "Often called 'indirect fillings,' these are used when a tooth is too damaged for a normal filling but doesn't require a full crown. They provide a custom-fit, durable solution for restoring tooth structure."
        }
    ];

    const lifespanData = [
        { type: "Dental Fillings", life: "5 – 15 years" },
        { type: "Crowns", life: "10 – 20 years" },
        { type: "Dental Bridges", life: "10 – 15 years" },
        { type: "Dental Implants", life: "20+ years" },
        { type: "Veneers", life: "10 – 15 years" }
    ];

    const maintenanceTips = [
        { icon: <FaBrush />, text: "Brush twice daily with fluoride toothpaste" },
        { icon: <FaCheckCircle />, text: "Floss regularly to remove hidden debris" },
        { icon: <FaExclamationTriangle />, text: "Avoid biting extremely hard foods" },
        { icon: <FaCalendarAlt />, text: "Visit your dentist every 6 months for checkups" },
        { icon: <FaShieldAlt />, text: "Use a mouthguard if you suffer from bruxism (tooth grinding)" }
    ];

    return (
        <>
            <Helmet>
                <title>Advanced Tooth Restoration in HSR Layout | Kosmix Dental Clinic</title>
                <meta name="description" content="Professional tooth restoration in HSR Layout, Bengaluru. Repair damaged, decayed, or missing teeth with advanced fillings, crowns, implants, and more." />
            </Helmet>

            <div className="service1-section">
                {/* HERO SECTION */}
                <div className="hero">
                    <h1 className="service1-title-text" data-aos="fade-down" data-aos-delay="200" style={{ fontFamily: 'initial' }}>
                        Expert Tooth Restoration in HSR Layout
                    </h1>

                    <div className="hero-content">
                        <div className="hero-image" data-aos="zoom-in-right" data-aos-delay="200" data-aos-duration="800">
                            <img src={img} alt="Advanced Tooth Restoration" />
                        </div>

                        <div className="hero-text" data-aos="zoom-in-left" data-aos-delay="200" data-aos-duration="800">
                            <p>
                                <strong>What is Tooth Restoration?</strong>
                            </p>
                            <p>
                                Tooth restoration is a dental procedure used to repair or replace damaged, decayed, or missing teeth. The goal is to restore the tooth’s normal shape, function, and appearance while preventing further dental problems.
                            </p>
                            <p>
                                When teeth are affected by cavities, fractures, infections, or wear, dentists use restorative treatments to rebuild the structure and ensure proper oral function. Modern dentistry offers several advanced techniques that provide natural-looking and long-lasting results.
                            </p>
                            <p>
                                At professional clinics such as <strong>Kosmix Dental Clinic</strong> in HSR Layout, Bengaluru, restorative dentistry combines advanced technology and specialized dental expertise to deliver effective treatments for patients of all ages.
                            </p>
                            <p>
                                Tooth restoration is not only about improving appearance—it is also essential for maintaining oral health, preventing infections, and restoring chewing ability.
                            </p>
                        </div>
                    </div>
                </div>

                {/* IMPORTANCE SECTION */}
                <section className="importance-section">
                    <div className="content" data-aos="fade-up">
                        <SectionTitle title="Why Tooth Restoration is Important" />
                        <p1 className="text_first">
                            Ignoring damaged teeth can lead to serious dental complications. Tooth restoration helps prevent these issues and improves overall oral health.
                        </p1>
                    </div>

                    <div className="importance-cards-container">
                        {benefits.map((item, idx) => (
                            <motion.div
                                className="importance-card"
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <h4>{item.benefit}</h4>
                                <p>{item.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="content" data-aos="fade-up" style={{ marginTop: '20px' }}>
                        <p style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
                            Without proper treatment, damaged teeth can lead to infections, gum disease, and even tooth loss.
                        </p>
                    </div>
                </section>

                {/* CAUSES SECTION */}
                <section className="advantages-section">
                    <div className="content" data-aos="fade-up">
                        <SectionTitle title="Common Causes of Tooth Damage" />
                        <p1 className="text_first">
                            Understanding the causes of dental damage helps in taking effective preventive measures for long-term health.
                        </p1>
                    </div>

                    <div className="advantages-grid">
                        {damageCauses.map((item, index) => (
                            <div key={index} className="advantage-card" data-aos="fade-up" data-aos-delay={index * 100}>
                                <div className="advantage-icon">{item.icon}</div>
                                <div>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* TREATMENTS GALLERY */}
                <section className="crown-types-section">
                    <div className="content" data-aos="fade-up">
                        <SectionTitle title="Types of Tooth Restoration Treatments" />
                        <p1 className="text_first">
                            Modern dentistry offers several restorative options depending on the severity of tooth damage.
                        </p1>
                    </div>

                    <div className="crown-grid restoration-grid">
                        {treatmentTypes.map((item, idx) => (
                            <div className="crown-card" key={idx} data-aos="zoom-in" data-aos-delay={idx * 150}>
                                <div className="icon">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PROCEDURE SECTION */}
                <section className="timeline-container">
                    <div className="content" data-aos="fade-up">
                        <SectionTitle title="The Tooth Restoration Procedure" />
                        <p1 className="text_first">
                            A systematic approach ensures each restoration is functional, comfortable, and aesthetically pleasing.
                        </p1>
                    </div>

                    <div style={{ marginTop: '50px' }}>
                        {[
                            { step: "01", title: "Dental Examination", desc: "The dentist examines the tooth and may take X-rays to assess the gravity of damage.", icon: <FaClinicMedical /> },
                            { step: "02", title: "Treatment Planning", desc: "A customized plan is created based specifically on the patient’s dental condition.", icon: <FaUserMd /> },
                            { step: "03", title: "Restoration Procedure", desc: "The damaged tooth is cleaned, repaired, or replaced using the chosen method.", icon: <FaTools /> },
                            { step: "04", title: "Final Adjustments", desc: "The dentist ensures proper bite alignment and patient comfort for the restoration.", icon: <FaCheckCircle /> }
                        ].map((item, index) => (
                            <div className="timeline-step" key={index} data-aos="fade-up">
                                <div className="timeline-icon">{item.icon}</div>
                                <div className="timeline-content" data-step={item.step}>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* LIFESPAN TABLE SECTION */}
                <section className="benefits-section" data-aos="fade-up">
                    <div className="benefits-wrapper">
                        <div className="content">
                            <h2 className="section-title">How Long Do Tooth Restorations Last?</h2>
                            <p style={{ textAlign: 'center', marginBottom: '20px' }}>The lifespan depends on the material used and consistent oral hygiene habits.</p>
                        </div>
                        <div className="table-responsive">
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Restoration Type</th>
                                        <th>Average Lifespan</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lifespanData.map((item, index) => (
                                        <tr key={index}>
                                            <td className="category-cell">{item.type}</td>
                                            <td>{item.life}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p style={{ marginTop: '20px', fontSize: '0.9rem', fontStyle: 'italic' }}>
                            * Proper care can significantly extend the longevity of these restorative treatments.
                        </p>
                    </div>
                </section>

                {/* MAINTENANCE TIPS */}
                <section className="why-choose-us">
                    <div className="wrapper">
                        <div className="image-side" data-aos="zoom-in-right">
                            <img src={dentalImage} alt="Oral Care and Maintenance" />
                        </div>

                        <div className="text-side" data-aos="zoom-in-left">
                            <h2>Tips to Maintain Restored Teeth</h2>
                            <p>Maintaining restored teeth requires consistent oral care to ensure longevity and prevent secondary decay.</p>
                            <div className="maintenance-list">
                                {maintenanceTips.map((tip, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                                        <span style={{ color: '#561b0f', fontSize: '1.2rem' }}>{tip.icon}</span>
                                        <span style={{ fontSize: '1.05rem' }}>{tip.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* WHO NEEDS SECTION */}
                <div className="content" data-aos="fade-up" style={{ padding: '60px 20px' }}>
                    <SectionTitle title="Who Needs Tooth Restoration?" />
                    <p1 className="text_first">
                        Tooth restoration is recommended for individuals experiencing any of the following dental issues:
                    </p1>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '30px', marginTop: '30px' }}>
                        {[
                            "Cavities or tooth decay",
                            "Broken or chipped teeth",
                            "Missing teeth",
                            "Severe tooth wear",
                            "Tooth infections"
                        ].map((item, i) => (
                            <div key={i} className="importance-card1" style={{ minWidth: '200px' }}>
                                <div className="card-icon"><FaCheckCircle /></div>
                                <h4>{item}</h4>
                            </div>
                        ))}
                    </div>
                    <p style={{ marginTop: '30px', textAlign: 'center' }}>
                        Early treatment helps prevent more complicated and expensive dental problems in the future.
                    </p>
                </div>

                {/* FAQ SECTION */}
                <section className="pricing-section" data-aos="fade-up">
                    <div className="pricing-content">
                        <div className="text-area">
                            <SectionTitle title="FAQs About Tooth Restoration" />
                            <div className="faq-container">
                                <div className="faq-item" style={{ marginBottom: '20px' }}>
                                    <p><strong>1. Is tooth restoration painful?</strong></p>
                                    <p>Most restorative dental procedures are performed under local anesthesia, making them virtually painless. Modern dental technology ensures a comfortable treatment experience.</p>
                                </div>
                                <div className="faq-item" style={{ marginBottom: '20px' }}>
                                    <p><strong>2. How long does a tooth restoration procedure take?</strong></p>
                                    <p>Simple restorations like fillings may take 30–60 minutes, while treatments like implants may require multiple visits over several months.</p>
                                </div>
                                <div className="faq-item" style={{ marginBottom: '20px' }}>
                                    <p><strong>3. Can restored teeth look natural?</strong></p>
                                    <p>Yes. Modern materials such as ceramic and composite resin are designed to match the natural color and shape of your existing teeth.</p>
                                </div>
                                <div className="faq-item" style={{ marginBottom: '20px' }}>
                                    <p><strong>4. What is the best tooth restoration treatment?</strong></p>
                                    <p>The best treatment depends on the severity of tooth damage. Your dentist will recommend the most suitable option after a thorough clinical examination.</p>
                                </div>
                                <div className="faq-item" style={{ marginBottom: '20px' }}>
                                    <p><strong>5. Are dental implants better than bridges?</strong></p>
                                    <p>Dental implants are considered more durable and natural-looking, but bridges may be recommended in certain cases depending on your bone density and oral health.</p>
                                </div>
                                <div className="faq-item">
                                    <p><strong>6. How much does tooth restoration cost?</strong></p>
                                    <p>Costs vary depending on the procedure, materials used, and complexity. Basic fillings are usually less expensive than implants or full mouth restorations.</p>
                                </div>
                            </div>
                        </div>

                        <div className="price-cards">
                            <div className="price-card premium">
                                <div className="tag premium-tag">Premium Care</div>
                                <h3 className="price"><MdRestore /> Custom Fit</h3>
                                <p className="desc">Restorative Excellence at Kosmix</p>
                                <ul className="details">
                                    <li>● Natural-looking Results</li>
                                    <li>● Durable Materials</li>
                                    <li>● Expert Clinical Team</li>
                                    <li>● Pain-Free Experience</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CONCLUSION */}
                <section className="content" data-aos="fade-up" style={{ padding: '20px', backgroundColor: '#fdf6ec', borderRadius: '16px', margin: '40px auto', maxWidth: '1000px' }}>
                    <h2 className="section-title">Conclusion</h2>
                    <p style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                        Tooth restoration plays a crucial role in modern dentistry by repairing damaged teeth and restoring oral health. Whether through fillings, crowns, implants, or veneers, restorative treatments help patients regain both function and confidence in their smiles. With advancements in dental technology and specialized expertise at Kosmix Dental Clinic, patients can now benefit from comfortable, effective, and long-lasting restorative solutions. Consulting a qualified dentist ensures the best treatment plan for maintaining a healthy and beautiful smile.
                    </p>
                </section>

                {/* CTA SECTION */}
                <section className="cta-section" data-aos='zoom-in'>
                    <div className="cta-content">
                        <h2>Ready to Restore Your Smile? <span>Book Now!</span></h2>
                        <p>Experience world-class tooth restoration treatments at Kosmix Dental Clinic in HSR Layout. Schedule your consultation today for a healthier smile.</p>
                        <div className="cta-buttons">
                            <Link to="/#appointment">
                                <button className="btn-primary">Book an Appointment</button>
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

const SectionTitle = ({ title }) => (
    <h2 className="section-title">{title}</h2>
);

export default ToothRestoration;
